import { useState, useCallback, useRef, useEffect } from 'react';
import { ActionMenu, ActionMenuItem, useActionMenu } from 'components/ActionMenu';
import useIntegrationSettingsModal from 'hooks/useIntegrationSettingsModal';
import { Box, IconButton, Chip } from '@mui/material';
import { MoreVertical, Settings } from 'react-feather';
import { useSelector } from 'react-redux';
import { selectAuthAccount } from 'AuthModule/selectors';
import { useUpdatePhaseOrderMutation } from 'IntegrationModule/api/queries/useUpdatePhaseOrderMutation';
import { useTransitionStateMutation } from 'IntegrationModule/api/queries/useTransitionStateMutation';
import { useDeleteIntegrationMutation } from 'IntegrationModule/api/queries/useDeleteIntegrationMutation';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import OdbcCredentialCheckModal from 'IntegrationModule/components/OdbcCredentialCheckModal';
import StateTransitionConfirmModal, {
  ACTION_TO_NEW_STATE,
} from 'IntegrationModule/components/StateTransitionConfirmModal';
import EditConnectionInfoModal from 'IntegrationModule/components/EditConnectionInfoModal';
import ChangeSyncFrequencyModal from 'IntegrationModule/components/ChangeSyncFrequencyModal';
import { Integration, IntegrationState } from 'types/integration';
import { hasNonArchivedCsvIntegration } from 'IntegrationModule/utils/utils';
import noop from 'lodash/noop';
import {
  CSV_ALREADY_EXISTS_TOOLTIP,
  CSV_TEAM_INTEGRATIONS_UNKNOWN_TOOLTIP,
  getIntegrationMenuItems,
  IntegrationTransitionAction,
} from 'IntegrationModule/components/IntegrationOptions/getIntegrationMenuItems';
import { useNotification } from 'hooks/useNotification';
import { checkIsCsvIntegration } from 'utils/integration';

const useIntegrationOptions = () => {
  const { anchorEl, anchorPosition, isOpen, openMenu, closeMenu } = useActionMenu();
  const { handleOpenIntegrationSettingsModal } = useIntegrationSettingsModal();
  const [menuItems, setMenuItems] = useState<ActionMenuItem[]>([]);
  // Track pending menu open to avoid flushSync - menu opens after items are set
  const pendingOpenRef = useRef<HTMLElement | null>(null);

  // Open menu after items are set (avoids flushSync anti-pattern)
  useEffect(() => {
    if (pendingOpenRef.current && menuItems.length > 0) {
      openMenu(pendingOpenRef.current);
      pendingOpenRef.current = null;
    }
  }, [menuItems, openMenu]);

  const handleOpenMenu = useCallback((element: HTMLElement, items: ActionMenuItem[]) => {
    pendingOpenRef.current = element;
    setMenuItems(items);
  }, []);

  return {
    IntegrationOptionsMenu: (
      <ActionMenu
        anchorEl={anchorEl}
        anchorPosition={anchorPosition}
        open={isOpen}
        onClose={closeMenu}
        items={menuItems}
      />
    ),
    handleOpenMenu,
    handleOpenIntegrationSettingsModal,
  };
};

export default useIntegrationOptions;

export const IntegrationOptions = ({
  handleOpenIntegrationSettingsModal,
  handleOpenMenu,
  targetServiceId,
  teamId,
  state,
  isBakIntegration = false,
  syncFrequencyUpdatedOnSuccess = noop,
  showSettingButton = true,
  showOdbcChip = false,
  showBakChip = false,
  targetService,
  customDailyFetchCount,
  customDailyFetchTime,
  tenant,
  realmId,
  teamIntegrations,
}: Omit<ReturnType<typeof useIntegrationOptions>, 'IntegrationOptionsMenu'> & {
  targetServiceId: number;
  teamId: number;
  state?: IntegrationState;
  isBakIntegration?: boolean;
  showSettingButton?: boolean;
  showOdbcChip?: boolean;
  showBakChip?: boolean;
  targetService?: string;
  /** Current daily sync count, used to seed the Change Sync Frequency modal. */
  customDailyFetchCount?: number;
  /** Current scheduled UTC sync times, used to seed the Change Sync Frequency modal. */
  customDailyFetchTime?: string | string[];
  tenant?: string;
  realmId?: string;
  /**
   * All of the team's integrations, used to block Unarchive on a csv-agent when the
   * team already has one (GLX-2091). Pass undefined until the list has loaded: a
   * csv-agent's Unarchive then stays disabled, since a duplicate can't be ruled out.
   */
  teamIntegrations?: Integration[];
  /**
   * Called after a successful sync-frequency change so redux-backed callers can
   * refetch the integration (the mutation only invalidates the react-query cache).
   */
  syncFrequencyUpdatedOnSuccess?: ({ targetServiceId }: { targetServiceId: number }) => void;
}) => {
  const account = useSelector(selectAuthAccount);
  const galaxyUserId = account?.id?.toString() || '0';
  const galaxyUserEmail = account?.email || '';

  const { updatePhaseOrder } = useUpdatePhaseOrderMutation();
  const { transitionState } = useTransitionStateMutation();
  const { deleteIntegration } = useDeleteIntegrationMutation();
  const { openModal } = useDynamicModal();
  const { showError } = useNotification();

  const isAjeraIntegration = targetService?.startsWith('deltek-ajera-');

  // `state` is the source of truth for integration lifecycle (GLX-1602).
  const effectiveState = state;

  const isCsvIntegration = checkIsCsvIntegration(targetService);
  // Until the team list has loaded, a second csv-agent can't be ruled out, so
  // Unarchive stays blocked (GLX-2091).
  const getCsvUnarchiveBlockedReason = () => {
    if (!teamIntegrations) return CSV_TEAM_INTEGRATIONS_UNKNOWN_TOOLTIP;
    return hasNonArchivedCsvIntegration(teamIntegrations, targetServiceId)
      ? CSV_ALREADY_EXISTS_TOOLTIP
      : undefined;
  };
  const csvUnarchiveBlockedReason = getCsvUnarchiveBlockedReason();

  // The menu items are built when the menu opens, so Unarchive can still be enabled
  // after the team list reloads and shows another csv-agent: recheck the latest list
  // before opening and before submitting the confirmation (GLX-2091).
  const csvUnarchiveBlockedReasonRef = useRef(csvUnarchiveBlockedReason);
  useEffect(() => {
    csvUnarchiveBlockedReasonRef.current = csvUnarchiveBlockedReason;
  }, [csvUnarchiveBlockedReason]);

  const isBlockedCsvUnarchive = useCallback(
    (action: IntegrationTransitionAction) => {
      const reason = csvUnarchiveBlockedReasonRef.current;
      if (action !== 'unarchive' || !isCsvIntegration || !reason) {
        return false;
      }
      showError(reason);
      return true;
    },
    [isCsvIntegration, showError],
  );

  const handleOpenOdbcCredentialCheckModal = useCallback(() => {
    openModal({
      modal: (modalProps) => <OdbcCredentialCheckModal {...modalProps} />,
    });
  }, [openModal]);

  const openConfirmModal = useCallback(
    (action: IntegrationTransitionAction) => {
      if (isBlockedCsvUnarchive(action)) return;
      openModal({
        modal: (modalProps) => (
          <StateTransitionConfirmModal
            {...modalProps}
            action={action}
            onConfirm={(reason) => {
              if (isBlockedCsvUnarchive(action)) {
                modalProps.toggle();
                return;
              }
              if (action === 'delete') {
                deleteIntegration({
                  targetServiceId,
                  galaxyUserId,
                  galaxyUserEmail,
                  reason,
                  tenant,
                  realmId,
                });
              } else {
                transitionState({
                  targetServiceId,
                  newState: ACTION_TO_NEW_STATE[action],
                  reason,
                  galaxyUserId,
                  galaxyUserEmail,
                  tenant,
                  realmId,
                });
              }
              modalProps.toggle();
            }}
          />
        ),
      });
    },
    [
      openModal,
      isBlockedCsvUnarchive,
      deleteIntegration,
      transitionState,
      targetServiceId,
      galaxyUserId,
      galaxyUserEmail,
      tenant,
      realmId,
    ],
  );

  const openEditConnectionInfoModal = useCallback(() => {
    openModal({
      modal: (modalProps) => (
        <EditConnectionInfoModal
          {...modalProps}
          targetServiceId={targetServiceId}
          teamId={teamId}
          serviceName={targetService || ''}
          isBakIntegration={isBakIntegration}
          tenant={tenant}
          realmId={realmId}
        />
      ),
    });
  }, [openModal, targetServiceId, teamId, targetService, isBakIntegration, tenant, realmId]);

  const openChangeSyncFrequencyModal = useCallback(() => {
    openModal({
      modal: (modalProps) => (
        <ChangeSyncFrequencyModal
          {...modalProps}
          mosaicTeamId={teamId}
          serviceName={targetService || ''}
          targetServiceId={targetServiceId}
          currentDailyFetchCount={customDailyFetchCount}
          currentDailyFetchTime={customDailyFetchTime}
          tenant={tenant}
          realmId={realmId}
          onUpdated={() => syncFrequencyUpdatedOnSuccess({ targetServiceId })}
        />
      ),
    });
  }, [
    openModal,
    teamId,
    targetService,
    targetServiceId,
    customDailyFetchCount,
    customDailyFetchTime,
    tenant,
    realmId,
    syncFrequencyUpdatedOnSuccess,
  ]);

  // Hide settings button for DRAFT state (not yet active)
  const shouldShowSettings = showSettingButton && effectiveState !== IntegrationState.DRAFT;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
        marginLeft: 3,
      }}
    >
      {showOdbcChip ? <Chip color="primary" label={'ODBC'} /> : <></>}
      {showBakChip ? <Chip color="primary" label={'.Bak Integration'} /> : <></>}
      {shouldShowSettings ? (
        <IconButton
          onClick={() => {
            handleOpenIntegrationSettingsModal({
              targetServiceId,
              teamId,
            });
          }}
          color="primary"
        >
          <Settings />
        </IconButton>
      ) : (
        <></>
      )}
      <Box
        onClick={(e) => {
          e.stopPropagation();

          const items = getIntegrationMenuItems({
            state: effectiveState,
            targetService,
            isAjeraIntegration: Boolean(isAjeraIntegration),
            csvUnarchiveBlockedReason,
            handlers: {
              onRetroactivePhaseOrder: () => updatePhaseOrder({ targetServiceId }),
              onCheckOdbcCredentials: handleOpenOdbcCredentialCheckModal,
              onChangeSyncFrequency: openChangeSyncFrequencyModal,
              onEditConnectionInfo: openEditConnectionInfoModal,
              onTransition: openConfirmModal,
            },
          });

          handleOpenMenu(e.currentTarget, items);
        }}
        sx={{
          mr: 5,
        }}
      >
        <IconButton>
          <MoreVertical />
        </IconButton>
      </Box>
    </Box>
  );
};
